import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SITE_MEDIA } from "@/constants/siteMedia";
import { navigateToHomeSection } from "@/utils/homeNavigation";
import { homeSectionPath, scrollToLiveDemoChat } from "@/constants/homeSections";

/** Menu entry (not react-router-dom’s NavLink component). */
type NavbarMenuLink = {
  label: string;
  href: string;
  isSection: boolean;
  sectionId?: string;
};

const navLinks: NavbarMenuLink[] = [
  { label: "Problem", href: "/problem", isSection: true, sectionId: "problem" },
  { label: "Solution", href: "/emotional-hit", isSection: true, sectionId: "emotional-hit" },
  { label: "How It Works", href: "/live-demo", isSection: true, sectionId: "live-demo" },
  { label: "Benefits", href: "/use-cases", isSection: true, sectionId: "use-cases" },
  { label: "Trust", href: "/trust", isSection: true, sectionId: "trust" },
  { label: "Why Ava Exists", href: "/why-ava", isSection: true, sectionId: "why-ava" },
  { label: "Social Proof", href: "/social-proof", isSection: true, sectionId: "social-proof" },
  { label: "Pricing", href: "/pricing", isSection: true, sectionId: "pricing" },
  { label: "FAQs", href: "/faqs", isSection: true, sectionId: "faqs" },
  { label: "Blog", href: "/blog", isSection: false },
  { label: "Contact", href: "/contact", isSection: false },
];

function HoverGradientNav({
  navigate,
  currentPathname,
}: {
  navigate: ReturnType<typeof useNavigate>;
  currentPathname: string;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoverStyle, setHoverStyle] = useState({ left: 0, width: 0 });
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const navRef = useRef<HTMLDivElement>(null);

  const updateHover = (index: number) => {
    const item = itemRefs.current[index];
    const nav = navRef.current;
    if (!item || !nav) return;
    const navRect = nav.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    setHoverStyle({
      left: itemRect.left - navRect.left,
      width: itemRect.width,
    });
    setHoveredIndex(index);
  };

  return (
    <div
      ref={navRef}
      className="relative hidden lg:flex items-center gap-0.5 px-2 py-1.5 rounded-full"
      style={{
        background: "rgba(255,255,255,0.6)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(124,58,237,0.12)",
      }}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      {hoveredIndex !== null && (
        <div
          className="absolute top-1 bottom-1 rounded-full pointer-events-none transition-all duration-200"
          style={{
            left: hoverStyle.left,
            width: hoverStyle.width,
            background: "linear-gradient(135deg, rgba(124,58,237,0.12), rgba(168,85,247,0.18))",
            border: "1px solid rgba(124,58,237,0.2)",
          }}
        />
      )}
      {navLinks.map((link, i) => (
        <a
          key={link.label}
          ref={(el) => {
            itemRefs.current[i] = el;
          }}
          href={link.href}
          onClick={(e) => {
            e.preventDefault();
            if (link.isSection && link.sectionId) {
              navigateToHomeSection(link.sectionId, navigate, currentPathname);
            } else {
              navigate(link.href);
            }
          }}
          className="relative z-10 px-3 py-2 text-xs font-medium text-[#5a4a8a] hover:text-[#7C3AED] transition-colors duration-200 cursor-pointer whitespace-nowrap rounded-full"
          onMouseEnter={() => updateHover(i)}
        >
          {link.label}
        </a>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomeRoot = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogoClick = useCallback(() => {
    if (isHomeRoot) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  }, [isHomeRoot, navigate]);

  const handleTalkToAva = useCallback(() => {
    setMenuOpen(false);
    const target = homeSectionPath("live-demo");
    const alreadyOnLiveDemo = location.pathname === target;
    navigateToHomeSection("live-demo", navigate, location.pathname);
    const delay = alreadyOnLiveDemo ? 200 : 850;
    window.setTimeout(() => {
      scrollToLiveDemoChat("smooth");
      window.dispatchEvent(new CustomEvent("ava-talk-now"));
    }, delay);
  }, [navigate, location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-[#e8e0ff]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        <button
          type="button"
          onClick={handleLogoClick}
          className="flex items-center gap-2.5 cursor-pointer flex-shrink-0 bg-transparent border-none"
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
          >
            <img
              src={SITE_MEDIA.avaFace512}
              alt="AVA Chat AI Logo"
              className="w-full h-full object-cover"
            />
          </div>
          <span className="font-display font-bold text-lg tracking-tight text-[#2d1b69]">
            AVA CHAT AI
          </span>
        </button>

        <div className="hidden lg:flex flex-1 justify-center mx-4">
          <HoverGradientNav navigate={navigate} currentPathname={location.pathname} />
        </div>

        <button
          type="button"
          className="hidden lg:flex whitespace-nowrap items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-all border-none"
          style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
          onClick={handleTalkToAva}
        >
          <i className="ri-chat-smile-3-line text-sm"></i>
          Talk to Ava
        </button>

        <button
          type="button"
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer text-[#2d1b69] bg-transparent border-none"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${menuOpen ? "close" : "menu"}-line text-xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-white/98 backdrop-blur-md border-t border-[#e8e0ff] px-4 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <button
              key={link.label}
              type="button"
              onClick={() => {
                setMenuOpen(false);
                if (link.isSection && link.sectionId) {
                  navigateToHomeSection(link.sectionId, navigate, location.pathname);
                } else {
                  navigate(link.href);
                }
              }}
              className="text-[#5a4a8a] font-medium py-3 px-3 rounded-xl cursor-pointer hover:text-[#7C3AED] hover:bg-[#f5f0ff] transition-all text-sm text-left bg-transparent border-none"
            >
              {link.label}
            </button>
          ))}
          <div className="pt-3 border-t border-[#e8e0ff] mt-2">
            <button
              type="button"
              onClick={handleTalkToAva}
              className="whitespace-nowrap w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full text-white text-sm font-semibold cursor-pointer border-none"
              style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
            >
              <i className="ri-chat-smile-3-line"></i>
              Talk to Ava now
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
